import {
    PiCheckCircleBold,
    PiClockBold,
    PiFolderOpenBold,
    PiFoldersBold,
    PiReceiptBold,
} from 'react-icons/pi';
import { cn } from '@v2/utils/cn';
import { PLOOMES_ATTACHMENT_CATEGORIES, SIDEBAR_ITEMS, type SidebarFilter } from './categories';
import type { FileManagerStats } from './types';

type Props = {
    active: SidebarFilter;
    onChange: (filter: SidebarFilter) => void;
    stats?: FileManagerStats;
};

function filterKey(filter: SidebarFilter): string {
    if (filter.type === 'category') return `cat:${filter.category}`;
    return filter.type;
}

function iconFor(key: string) {
    switch (key) {
        case 'all':       return PiFoldersBold;
        case 'receipt':   return PiReceiptBold;
        case 'pending':   return PiClockBold;
        case 'validated': return PiCheckCircleBold;
        default:          return PiFolderOpenBold;
    }
}

function countFor(filter: SidebarFilter, stats?: FileManagerStats): number | undefined {
    if (!stats) return undefined;
    switch (filter.type) {
        case 'all':       return stats.total;
        case 'receipts':  return stats.receipts;
        case 'pending':   return stats.pending;
        case 'validated': return stats.validated;
        case 'category':  return stats.by_category[filter.category] ?? 0;
    }
}

export default function FileManagerSidebar({ active, onChange, stats }: Props) {
    const activeKey = filterKey(active);

    return (
        <aside className="flex h-full flex-col border-r border-gray-200 bg-gray-50/80 p-4 dark:border-gray-700/60 dark:bg-gray-800/40">
            <p className="mb-3 px-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Acesso rápido
            </p>
            <nav className="space-y-0.5">
                {SIDEBAR_ITEMS.map((item) => (
                    <SidebarButton
                        key={filterKey(item.filter)}
                        label={item.label}
                        icon={iconFor(item.icon)}
                        count={countFor(item.filter, stats)}
                        active={activeKey === filterKey(item.filter)}
                        onClick={() => onChange(item.filter)}
                    />
                ))}
            </nav>

            <p className="mb-3 mt-6 px-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Pastas
            </p>
            <nav className="space-y-0.5 overflow-y-auto">
                {Object.entries(PLOOMES_ATTACHMENT_CATEGORIES).map(([key, label]) => {
                    const filter: SidebarFilter = { type: 'category', category: key };
                    return (
                        <SidebarButton
                            key={key}
                            label={label}
                            icon={PiFolderOpenBold}
                            count={stats?.by_category[key]}
                            active={activeKey === filterKey(filter)}
                            onClick={() => onChange(filter)}
                        />
                    );
                })}
            </nav>
        </aside>
    );
}

function SidebarButton({
    label,
    icon: Icon,
    count,
    active,
    onClick,
}: {
    label: string;
    icon: React.ComponentType<{ className?: string }>;
    count?: number;
    active: boolean;
    onClick: () => void;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={cn(
                'flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm transition',
                active
                    ? 'bg-white font-semibold text-gray-900 shadow-sm ring-1 ring-gray-200 dark:bg-gray-700 dark:text-gray-100 dark:ring-gray-600'
                    : 'text-gray-600 hover:bg-white/70 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-700/50 dark:hover:text-gray-200',
            )}
        >
            <Icon className={cn('size-4 shrink-0', active ? 'text-gray-900 dark:text-gray-100' : 'text-gray-400 dark:text-gray-500')} />
            <span className="min-w-0 flex-1 truncate">{label}</span>
            {count !== undefined && (
                <span className={cn('shrink-0 text-xs tabular-nums', active ? 'text-gray-700 dark:text-gray-300' : 'text-gray-400 dark:text-gray-500')}>
                    {count}
                </span>
            )}
        </button>
    );
}
